import React, { useMemo } from 'react';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../../components/ui/ModalHeader';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import type { ClearApptDatesPreview } from '../../services/orders.service';

interface ClearApptDatesModalProps {
  preview: ClearApptDatesPreview;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Normalized YYYY-MM-DD -> M/D/YY for display; blank -> em dash. */
const fmt = (iso: string): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
};

/** Title-case a move action (PICKUP -> Pickup, TERMINATE -> Terminate); blank -> fallback. */
const moveLabel = (value: string, fallback: string): string => {
  const s = (value || '').trim();
  if (!s) return fallback;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

/**
 * Confirm dialog for the LC Dashboard "Clear All Leg Appt Dates" button
 * (Past Due - Not Dispatched bucket). Lists the pros/legs/dates staged to clear and any
 * pros skipped for an assigned driver. Pros whose delivery appointment is being removed
 * get a badge: an (uncoded) service failure will be created silently on save for the LC
 * to code later from the Service Failures dashboard — no manual entry here.
 */
const ClearApptDatesModal: React.FC<ClearApptDatesModalProps> = ({
  preview,
  saving,
  onCancel,
  onConfirm,
}) => {
  const rowsWithClears = useMemo(
    () => preview.rows.filter((r) => r.clears.length > 0),
    [preview.rows],
  );
  const skippedRows = useMemo(
    () => preview.rows.filter((r) => r.clears.length === 0 && (r.skippedLegnums.length > 0 || !!r.error)),
    [preview.rows],
  );
  const legCount = useMemo(
    () => rowsWithClears.reduce((n, r) => n + r.clears.length, 0),
    [rowsWithClears],
  );
  const sfCount = useMemo(
    () => rowsWithClears.filter((r) => r.serviceFailureRequired).length,
    [rowsWithClears],
  );

  const confirmDisabled = saving || rowsWithClears.length === 0;

  // Rendered only while open. Alt+C cancels; clearing appointment dates across
  // the selected rows stays click-only.
  useAltHotkeys({ C: saving ? null : onCancel }, undefined, { layer: true });

  // Rendered only while open (parent unmounts on close) — unmount resets the drag.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="clear-appt-dates-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col border border-slate-200 dark:border-gray-700"
      >
        <ModalHeader
          title="Clear All Leg Appt Dates"
          titleId="clear-appt-dates-title"
          onClose={saving ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
        />

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-sm text-label">
            Remove appointment dates for these pros?
          </p>

          {rowsWithClears.length > 0 ? (
            <div className="rounded border border-slate-200 dark:border-gray-700 divide-y divide-slate-100 dark:divide-gray-700">
              {rowsWithClears.map((row) => (
                <div key={String(row.pronumber)} className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-body">
                      Pro {row.pronumber}
                    </span>
                    {row.serviceFailureRequired && (
                      <span
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                        title="A service failure will be created for the removed delivery appointment. Code it later in the Service Failures dashboard."
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Service Failure
                      </span>
                    )}
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {row.clears.map((c) => (
                      <li key={c.legnum} className="text-xs text-gray-600 dark:text-gray-400">
                        Leg {c.legnum}: {moveLabel(c.legType, 'Orig')} {fmt(c.origApptDate)} ·{' '}
                        {moveLabel(c.legAction2, 'Dest')} {fmt(c.destApptDate)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Nothing to clear in the current view.
            </p>
          )}

          {rowsWithClears.length > 0 && (
            <p className="text-xs text-muted">
              {legCount} leg{legCount === 1 ? '' : 's'} across {rowsWithClears.length} pro
              {rowsWithClears.length === 1 ? '' : 's'} · appointment times are preserved.
              {sfCount > 0 && (
                <>
                  {' '}
                  {sfCount} service failure{sfCount === 1 ? '' : 's'} will be created
                  automatically (uncoded) for the LC dashboard.
                </>
              )}
            </p>
          )}

          {skippedRows.length > 0 && (
            <div className="rounded border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 px-3 py-2">
              <div className="text-xs font-semibold text-muted mb-1">
                Skipped
              </div>
              <ul className="space-y-0.5">
                {skippedRows.map((row) => (
                  <li key={String(row.pronumber)} className="text-xs text-gray-600 dark:text-gray-400">
                    Pro {row.pronumber} —{' '}
                    {row.error
                      ? row.error
                      : `driver assigned on leg${row.skippedLegnums.length === 1 ? '' : 's'} ${row.skippedLegnums.join(', ')}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-label bg-panel border border-slate-300 rounded hover:bg-slate-50 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => !confirmDisabled && onConfirm()}
            disabled={confirmDisabled}
            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-red-600 rounded hover:bg-red-700 disabled:opacity-50"
          >
            {saving ? 'Clearing…' : 'Clear Appt Dates'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ClearApptDatesModal;
